// Canvas pieces of the holding page (HoldingPage.jsx; prototype
// docs/holding-page-prototype/): the hero's night sky with the SacredMothr
// wandering like a firefly over a dark planet (the ship itself is SVG, from
// the app's start screen, voyageStartEngine.js), and First Light, the
// founding star. Both draw only while on screen and respect reduced motion.

const reducedMotion = () => {
  try { return window.matchMedia('(prefers-reduced-motion: reduce)').matches; } catch { return false; }
};

// The ship's wings are drawn twice (the right one mirrored) rather than with
// <use>, because styles do not reach inside <use> copies.
const WING = `<g>
  <path class="wing-glass" d="M176 128 C150 130 102 134 76 150 C60 162 62 190 84 198 C112 208 150 186 176 158 Z"/>
  <path class="filigree edge" d="M176 128 C150 130 102 134 76 150 C60 162 62 190 84 198 C112 208 150 186 176 158"/>
  <path class="filigree" d="M172 140 C148 150 116 166 86 190 M172 150 C150 162 128 178 108 196"/>
  <ellipse class="filigree" cx="104" cy="172" rx="12" ry="9" transform="rotate(-20 104 172)"/>
  <circle class="filigree" cx="104" cy="172" r="3.5"/>
  <path class="circuit slow" d="M172 142 L142 152 L114 166 L94 186"/>
  <circle class="node" cx="114" cy="166" r="1.3"/>
  <path class="wing-glass" d="M176 104 C150 76 100 46 36 50 C24 51 20 62 28 72 C48 94 88 114 120 124 C140 130 160 133 176 134 Z"/>
  <path class="filigree edge" d="M176 104 C150 76 100 46 36 50 C24 51 20 62 28 72 C48 94 88 114 120 124 C140 130 160 133 176 134"/>
  <path class="filigree" d="M172 110 C140 86 96 62 42 56 M172 118 C136 102 90 84 34 66 M172 126 C142 120 104 108 70 98"/>
  <path class="filigree" d="M56 60 C70 76 86 92 110 108 M84 54 C96 70 116 90 140 104"/>
  <ellipse class="filigree" cx="96" cy="82" rx="15" ry="10" transform="rotate(-25 96 82)"/>
  <ellipse class="filigree" cx="96" cy="82" rx="7" ry="4.6" transform="rotate(-25 96 82)"/>
  <path class="circuit" d="M172 116 L130 96 L96 90 L58 72"/>
  <path class="circuit gold slow" d="M172 124 C140 116 104 106 60 92"/>
  <circle class="node" cx="96" cy="90" r="1.4"/><circle class="node" cx="58" cy="72" r="1.2"/>
</g>`;
const ANTENNA = `<path class="filigree" d="M178 60 C170 44 156 34 140 30"/><path class="filigree" d="M172 50 l-5 -6 M172 50 l-7 1 M166 43 l-4 -6 M166 43 l-7 0 M159 37 l-3 -6 M159 37 l-7 -1 M151 33 l-2 -5 M151 33 l-6 -2"/>`;

// Fixed markup, no traveller input.
export const SHIP_MARKUP = `
  <svg class="layer glow" viewBox="0 0 360 250">
    <defs>
      <radialGradient id="hp-coreGlow"><stop offset="0" stop-color="#ff9d00" stop-opacity="0.75"/><stop offset="0.45" stop-color="#ff9d00" stop-opacity="0.18"/><stop offset="1" stop-color="#ff9d00" stop-opacity="0"/></radialGradient>
      <radialGradient id="hp-bellyGlow" cx="0.5" cy="0.3"><stop offset="0" stop-color="#ff7a00" stop-opacity="0.35"/><stop offset="1" stop-color="#ff7a00" stop-opacity="0"/></radialGradient>
      <radialGradient id="hp-wingGlass" cx="1" cy="0.55" r="1.1"><stop offset="0" stop-color="#ffb347" stop-opacity="0.32"/><stop offset="0.45" stop-color="#d4af37" stop-opacity="0.12"/><stop offset="1" stop-color="#2dd4bf" stop-opacity="0.05"/></radialGradient>
      <linearGradient id="hp-bodyShade" x1="0" y1="0" x2="1" y2="0"><stop offset="0" stop-color="#1a1408"/><stop offset="0.42" stop-color="#7a5418"/><stop offset="0.55" stop-color="#ffcc80"/><stop offset="0.7" stop-color="#7a5418"/><stop offset="1" stop-color="#140f06"/></linearGradient>
    </defs>
    <ellipse class="belly-glow" cx="180" cy="150" rx="120" ry="80"/>
    <circle class="core-glow" cx="180" cy="128" r="150"/>
  </svg>
  <svg class="layer wing wing-left" viewBox="0 0 360 250">${WING}</svg>
  <svg class="layer wing wing-right" viewBox="0 0 360 250"><g transform="translate(360 0) scale(-1 1)">${WING}</g></svg>
  <svg class="layer body" viewBox="0 0 360 250">
    <path class="body-shade" d="M180 58 C186 64 188 76 186 88 C192 100 194 118 190 134 C196 152 192 182 180 206 C168 182 164 152 170 134 C166 118 168 100 174 88 C172 76 174 64 180 58 Z"/>
    <path class="filigree edge" d="M180 58 C186 64 188 76 186 88 C192 100 194 118 190 134 C196 152 192 182 180 206 C168 182 164 152 170 134 C166 118 168 100 174 88 C172 76 174 64 180 58 Z"/>
    <path class="filigree" d="M176 150 L184 150 M175 162 L185 162 M176 174 L184 174 M177 186 L183 186"/>
    <g>${ANTENNA}</g><g transform="translate(360 0) scale(-1 1)">${ANTENNA}</g>
    <circle class="node" cx="180" cy="74" r="2"/>
    <path class="filigree" d="M180 100 L192 128 L180 156 L168 128 Z"/>
    <circle class="filigree" cx="180" cy="128" r="16"/>
    <circle class="filigree" cx="180" cy="128" r="22" stroke-dasharray="2 4"/>
    <circle class="core" cx="180" cy="128" r="12"/>
  </svg>`;

// Runs draw(now, dt) every frame while the canvas is on screen and the tab is
// visible. Returns a stop function.
function animate(canvas, draw) {
  let raf = 0, last = performance.now(), visible = true, stopped = false;
  const io = typeof IntersectionObserver === 'function'
    ? new IntersectionObserver(([e]) => { visible = e.isIntersecting; })
    : null;
  io?.observe(canvas);
  const loop = (now) => {
    if (stopped) return;
    const dt = Math.min(0.05, (now - last) / 1000); last = now;
    if (visible && !document.hidden) draw(now, dt);
    raf = requestAnimationFrame(loop);
  };
  raf = requestAnimationFrame(loop);
  return () => { stopped = true; cancelAnimationFrame(raf); io?.disconnect(); };
}

// The hero: drifting stars, a nebula wash, a dark planet lit along its rim,
// and the ship's slow wandering flight with a fading trail. `flight` is the
// positioned wrapper, `ship` the element that banks.
export function mountHeroSky(canvas, { flight, ship }) {
  const ctx = canvas.getContext('2d');
  if (!ctx) return () => {};
  const reduced = reducedMotion();
  const stars = [], trail = [];
  let W = 0, H = 0, prevX = 0, tick = 0;

  function resize() {
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    W = canvas.clientWidth; H = canvas.clientHeight;
    canvas.width = Math.max(1, W * dpr); canvas.height = Math.max(1, H * dpr);
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    stars.length = 0;
    const n = Math.round((W * H) / 1400);
    for (let i = 0; i < n; i++) {
      const layer = i % 3;
      stars.push({ x: Math.random() * W, y: Math.random() * H, r: [0.3, 0.55, 0.95][layer] * (0.7 + Math.random() * 0.6), s: [3, 7, 14][layer], a: [0.35, 0.55, 0.85][layer] * (0.6 + Math.random() * 0.4), tw: Math.random() * 6 });
    }
  }

  function drawPlanet() {
    const r = Math.max(W * 1.05, 520), cx = W * 0.8, cy = H * 0.86 + r;
    const haze = ctx.createRadialGradient(cx, cy, r * 0.96, cx, cy, r * 1.08);
    haze.addColorStop(0, 'rgba(255, 170, 80, 0.12)'); haze.addColorStop(1, 'rgba(255, 170, 80, 0)');
    ctx.fillStyle = haze; ctx.beginPath(); ctx.arc(cx, cy, r * 1.08, 0, Math.PI * 2); ctx.fill();
    const body = ctx.createRadialGradient(cx - r * 0.3, cy - r * 0.9, r * 0.1, cx, cy, r);
    body.addColorStop(0, '#1d1b1d'); body.addColorStop(0.5, '#111214'); body.addColorStop(1, '#0b0c0e');
    ctx.fillStyle = body; ctx.beginPath(); ctx.arc(cx, cy, r, 0, Math.PI * 2); ctx.fill();
    const rim = ctx.createLinearGradient(cx - r * 0.7, cy - r, cx + r * 0.3, cy - r * 0.6);
    rim.addColorStop(0, 'rgba(255, 190, 110, 0.8)'); rim.addColorStop(0.55, 'rgba(255, 157, 0, 0.25)'); rim.addColorStop(1, 'rgba(255, 157, 0, 0)');
    ctx.strokeStyle = rim; ctx.lineWidth = 1.6; ctx.beginPath(); ctx.arc(cx, cy, r, Math.PI * 1.05, Math.PI * 1.75); ctx.stroke();
  }

  // Computers: to the right of the words. Phones: in the clear band above them.
  function placeShip(t) {
    const wide = W > 760, ax = wide ? 0.66 : 0.5, ay = wide ? 0.34 : 82 / Math.max(H, 1);
    flight.style.left = `${ax * 100}%`; flight.style.top = `${ay * 100}%`;
    if (reduced) { ship.style.transform = 'rotateX(38deg)'; return; }
    const k = t * 0.00016;
    const x = Math.sin(k * 0.7) * W * (wide ? 0.16 : 0.24) + Math.sin(k * 1.9) * W * 0.04;
    const y = Math.sin(k * 1.1) * (wide ? H * 0.07 : 22) + Math.sin(k * 2.3) * (wide ? H * 0.02 : 8);
    const vx = x - prevX; prevX = x;
    const bank = Math.max(-14, Math.min(14, vx * 9));
    flight.style.transform = `translate(calc(-50% + ${x.toFixed(1)}px), calc(-50% + ${y.toFixed(1)}px))`;
    ship.style.transform = `rotateX(38deg) rotateY(${(bank * 2.2).toFixed(2)}deg) rotateZ(${(bank * 0.6).toFixed(2)}deg)`;
    if ((tick = (tick + 1) % 12) === 0) {
      trail.push({ x: W * ax + x, y: H * ay + y + 3 });
      if (trail.length > 45) trail.shift();
    }
  }

  function draw(t, dt) {
    ctx.fillStyle = '#121316'; ctx.fillRect(0, 0, W, H);
    const nx = W * (0.3 + Math.sin(t * 0.00004) * 0.05), ny = H * 0.3;
    let g = ctx.createRadialGradient(nx, ny, 0, nx, ny, Math.max(W, H) * 0.6);
    g.addColorStop(0, 'rgba(45, 212, 191, 0.07)'); g.addColorStop(0.5, 'rgba(70, 60, 140, 0.06)'); g.addColorStop(1, 'rgba(18, 19, 22, 0)');
    ctx.fillStyle = g; ctx.fillRect(0, 0, W, H);
    const mx = W * 0.75, my = H * (0.7 + Math.cos(t * 0.00003) * 0.04);
    g = ctx.createRadialGradient(mx, my, 0, mx, my, Math.max(W, H) * 0.5);
    g.addColorStop(0, 'rgba(255, 157, 0, 0.06)'); g.addColorStop(1, 'rgba(18, 19, 22, 0)');
    ctx.fillStyle = g; ctx.fillRect(0, 0, W, H);
    for (const s of stars) {
      if (!reduced) { s.x -= s.s * dt; if (s.x < -2) { s.x = W + 2; s.y = Math.random() * H; } }
      ctx.globalAlpha = s.a * (0.75 + 0.25 * Math.sin(t * 0.001 + s.tw)); ctx.fillStyle = '#f3ead2';
      ctx.beginPath(); ctx.arc(s.x, s.y, s.r, 0, Math.PI * 2); ctx.fill();
    }
    ctx.globalAlpha = 1;
    drawPlanet();
    for (let i = 0; i < trail.length; i++) {
      const q = trail[i], f = i / trail.length;
      ctx.globalAlpha = f * f * 0.45; ctx.fillStyle = '#ffb347';
      ctx.beginPath(); ctx.arc(q.x, q.y, 0.5 + f * 1.2, 0, Math.PI * 2); ctx.fill();
    }
    ctx.globalAlpha = 1;
    placeShip(t);
  }

  const ro = typeof ResizeObserver === 'function' ? new ResizeObserver(resize) : null;
  ro?.observe(canvas);
  resize();
  draw(performance.now(), 0);
  const stop = animate(canvas, draw);
  return () => { stop(); ro?.disconnect(); };
}

// First Light: one star with soft diffraction spikes, breathing among faint dust.
export function mountFirstLight(canvas) {
  const ctx = canvas.getContext('2d');
  if (!ctx) return () => {};
  const reduced = reducedMotion();
  let W = 0, H = 0, dust = [];
  function resize() {
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    W = canvas.clientWidth; H = canvas.clientHeight;
    canvas.width = Math.max(1, W * dpr); canvas.height = Math.max(1, H * dpr);
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    dust = Array.from({ length: Math.round((W * H) / 900) }, () => ({ x: Math.random() * W, y: Math.random() * H, r: Math.random() * 0.9 + 0.2, a: Math.random() * 0.5 + 0.1, tw: Math.random() * 6 }));
  }
  function draw(t) {
    ctx.clearRect(0, 0, W, H);
    for (const d of dust) {
      ctx.globalAlpha = d.a * (0.7 + 0.3 * Math.sin(t * 0.0012 + d.tw)); ctx.fillStyle = '#f3ead2';
      ctx.beginPath(); ctx.arc(d.x, d.y, d.r, 0, Math.PI * 2); ctx.fill();
    }
    ctx.globalAlpha = 1;
    const x = W * 0.62, y = H * 0.32, pulse = reduced ? 1 : 0.92 + 0.08 * Math.sin(t * 0.0011);
    const R = Math.min(W, H) * 0.42 * pulse;
    let g = ctx.createRadialGradient(x, y, 0, x, y, Math.max(R, 1));
    g.addColorStop(0, 'rgba(255, 213, 138, 0.55)'); g.addColorStop(0.18, 'rgba(255, 170, 70, 0.18)'); g.addColorStop(1, 'rgba(255, 157, 0, 0)');
    ctx.fillStyle = g; ctx.fillRect(0, 0, W, H);
    ctx.save(); ctx.translate(x, y); ctx.rotate(reduced ? 0.2 : 0.2 + t * 0.00002);
    for (const [len, w, a] of [[R * 1.15, 1.4, 0.55], [R * 0.6, 1, 0.35]]) {
      for (let k = 0; k < 4; k++) {
        ctx.rotate(Math.PI / 2);
        const lg = ctx.createLinearGradient(0, 0, Math.max(len, 1), 0);
        lg.addColorStop(0, `rgba(255, 230, 180, ${a})`); lg.addColorStop(1, 'rgba(255, 230, 180, 0)');
        ctx.strokeStyle = lg; ctx.lineWidth = w; ctx.beginPath(); ctx.moveTo(0, 0); ctx.lineTo(len, 0); ctx.stroke();
      }
      ctx.rotate(Math.PI / 4);
    }
    ctx.restore();
    g = ctx.createRadialGradient(x, y, 0, x, y, 9 * pulse);
    g.addColorStop(0, '#fffaf0'); g.addColorStop(0.5, '#ffd58a'); g.addColorStop(1, 'rgba(255, 180, 80, 0)');
    ctx.fillStyle = g; ctx.beginPath(); ctx.arc(x, y, 9 * pulse, 0, Math.PI * 2); ctx.fill();
  }
  const ro = typeof ResizeObserver === 'function' ? new ResizeObserver(resize) : null;
  ro?.observe(canvas);
  resize();
  draw(performance.now());
  const stop = reduced ? () => {} : animate(canvas, (now) => draw(now));
  return () => { stop(); ro?.disconnect(); };
}
